import { defineStore } from "pinia";
import { store } from "@/store";
import { userType } from "./types";
import { routerArrays } from "@/layout/types";
import { resetRouter, router } from "@/router";
import { storageSession } from "@pureadmin/utils";
import { fetchAutoLogin, fetchLogin, fetchRefreshToken } from "@/api/user";
import { useMultiTagsStoreHook } from "@/store/modules/multiTags";
import { type DataInfo, removeToken, sessionKey, setToken, UserInfo } from "@/utils/auth";
import { RefreshTokenResult, UserResult } from "@/api/user.d";
import { ResponseResult } from "@/api/common.d";
import { useAuth } from "@/utils/authLogin";

const { logout } = useAuth();

export const useUserStore = defineStore({
  id: "pure-user",
  state: (): userType => ({
    // 用户信息
    user: storageSession().getItem<DataInfo<number>>(sessionKey)?.user
  }),
  getters: {
    isNotary(state) {
      return state.user?.roles?.name?.includes?.("公证员");
    },
    isAssistantNotary(state) {
      return state.user?.roles?.name === "公证员助理";
    },
    canAnswerVideo() {
      if (!this.isNotary) return false;
      const menu = storageSession().getItem<IUserMenu[]>("async-routes");
      for (const menuItem of menu) {
        if (menuItem.name === "Signature") {
          for (const child of menuItem.children) {
            if (child.name === "RemoteVideo") return true;
          }
        }
      }

      return false;
    }
  },
  actions: {
    /** 存储用户名 */
    SET_USER(user: UserInfo) {
      this.user = user;
    },
    async autoLogin(tk: string) {
      return new Promise<ResponseResult<UserResult>>((resolve, reject) => {
        fetchAutoLogin(tk.toString())
          .then((res) => {
            if (res.success) {
              setToken(res.data);
              resolve(res);
            } else {
              reject(false);
            }
          })
          .catch((error) => {
            reject(false);
          });
      });
    },
    /** 登入 */
    async loginByUsername(data) {
      return new Promise<ResponseResult<UserResult>>((resolve, reject) => {
        fetchLogin(data)
          .then((res) => {
            if (res.success) {
              setToken(res.data);
              resolve(res);
            } else {
              reject(res.message);
            }
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
    /** 前端登出（不调用接口） */
    logOut() {
      this.user = {};
      removeToken();
      useMultiTagsStoreHook().handleTags("equal", [...routerArrays]);
      resetRouter();
      logout();
      router.push("/login");
    },
    /** 刷新`token` */
    async handRefreshToken(data) {
      return new Promise<ResponseResult<RefreshTokenResult>>((resolve, reject) => {
        fetchRefreshToken(data)
          .then((res) => {
            if (res && res.data) {
              setToken(res.data);
              resolve(res);
            } else {
              reject(new Error("刷新token失败"));
            }
          })
          .catch((error) => {
            reject(error);
          });
      });
    }
  }
});

export function useUserStoreHook() {
  return useUserStore(store);
}
